import axios from "axios";
import { queueLaunch } from "./offline";

// Prefer same-origin `/api` so the built-in ingress proxy handles the backend
// hop — this avoids the cross-origin CORS trap where the CDN rewrites
// `Access-Control-Allow-Origin: *` while the client sends
// `withCredentials: true` (Chrome rejects that combination). Same-origin
// requests skip CORS entirely. Falls back to REACT_APP_BACKEND_URL for
// server-side / non-browser contexts (SSR, tests).
const ENV_BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const BACKEND_URL =
  typeof window !== "undefined" && window.location && window.location.origin
    ? window.location.origin
    : ENV_BACKEND;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true, timeout: 20000 });

export const fetchHome = () => api.get("/apps/home").then((r) => r.data);
export const fetchCategories = () => api.get("/categories").then((r) => r.data);
export const fetchApps = (params) => api.get("/apps", { params }).then((r) => r.data);
export const fetchApp = (slug) => api.get(`/apps/${slug}`).then((r) => r.data);
export const fetchFeed = (params) => api.get("/feed", { params }).then((r) => r.data);

// auth
export const authRegister = (data) => api.post("/auth/register", data).then((r) => r.data);
export const authLogin = (data) => api.post("/auth/login", data).then((r) => r.data);
export const authLogout = () => api.post("/auth/logout").then((r) => r.data);
export const authMe = () => api.get("/auth/me").then((r) => r.data);

// coins & tips
export const fetchCoinPackages = () => api.get("/coins/packages").then((r) => r.data);
export const purchaseCoins = (package_id) => api.post("/coins/purchase", { package_id, origin_url: window.location.origin }).then((r) => r.data);
export const tipDeveloper = (slug, coins, message) => api.post("/coins/tip", { slug, coins, message }).then((r) => r.data);
export const devSubscribe = () => api.post("/dev/subscribe", { origin_url: window.location.origin }).then((r) => r.data);
export const devToggleAutoRenew = (auto_renew) => api.post("/dev/subscription/auto-renew", { auto_renew }).then((r) => r.data);
export const devCancelSub = () => api.post("/dev/subscription/cancel").then((r) => r.data);

// social
export const likeApp = (slug) => api.post("/social/like", { slug }).then((r) => r.data);
export const saveApp = (slug) => api.post("/social/save", { slug }).then((r) => r.data);
export const socialStatus = (slug) => api.get(`/social/status/${slug}`).then((r) => r.data);
export const myLibrary = () => api.get("/me/library").then((r) => r.data);
export const myTips = () => api.get("/me/tips").then((r) => r.data);

// developer console
export const devPreview = (mode, value) => api.post("/dev/preview", { mode, value }).then((r) => r.data);
export const devPublishUrl = (data) => api.post("/dev/publish/url", data).then((r) => r.data);
export const devPublishGithub = (data) => api.post("/dev/publish/github", data).then((r) => r.data);
export const devPublishZip = (formData) => api.post("/dev/publish/zip", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
export const devMyApps = () => api.get("/dev/apps").then((r) => r.data);
export const devDeleteApp = (slug) => api.delete(`/dev/apps/${slug}`).then((r) => r.data);

// comments
export const listComments = (slug) => api.get(`/comments/${slug}`).then((r) => r.data);
export const createComment = (slug, body, parent_id) => api.post("/comments", { slug, body, parent_id }).then((r) => r.data);
export const likeComment = (id) => api.post(`/comments/${id}/like`).then((r) => r.data);
export const tipComment = (id, coins) => api.post(`/comments/${id}/tip`, { coins }).then((r) => r.data);

// follow
export const toggleFollow = (developer_slug) => api.post("/follow", { developer_slug }).then((r) => r.data);
export const followStatus = (developer_slug) => api.get(`/follow/status/${developer_slug}`).then((r) => r.data);
export const myFollowing = () => api.get("/me/following").then((r) => r.data);

// tracking + smart feed
export const trackEvent = (slug, event, seconds) => api.post("/track", { slug, event, seconds }).catch(() => {});
export const fetchSmartFeed = (params) => api.get("/feed/smart", { params }).then((r) => r.data);

// notifications
export const listNotifications = (limit = 50) => api.get("/notifications", { params: { limit } }).then((r) => r.data);
export const unreadCount = () => api.get("/notifications/unread-count").then((r) => r.data);
export const markNotifRead = (id) => api.post(`/notifications/${id}/read`).then((r) => r.data);
export const markAllRead = () => api.post("/notifications/read-all").then((r) => r.data);

// analytics
export const appAnalytics = (slug, days = 30) => api.get(`/dev/apps/${slug}/analytics`, { params: { days } }).then((r) => r.data);
export const devAnalyticsOverview = () => api.get("/dev/analytics/overview").then((r) => r.data);

// featured slots
export const fetchFeaturedSlots = () => api.get("/featured").then((r) => r.data);
export const buyFeaturedSlot = (slug) => api.post("/dev/apps/feature/buy", { slug }).then((r) => r.data);

// ads to earn
export const adStatus = () => api.get("/ads/status").then((r) => r.data);
export const watchAd = (duration_sec) => api.post("/ads/watch", { duration_sec }).then((r) => r.data);
// in-game ad claim (unlimited, skippable)
export const claimGameAd = (game, duration_sec = 5.0) =>
  api.post("/ads/game-claim", { game, duration_sec }).then((r) => r.data);

// multiplayer
export const createMpChallenge = (game) =>
  api.post("/mp/challenge", null, { params: { game } }).then((r) => r.data);
export const mpWsUrl = (game, { stake = 0, challenge = null } = {}) => {
  const base = BACKEND_URL.replace(/^http/, "ws");
  const qs = new URLSearchParams();
  if (stake) qs.set("stake", String(stake));
  if (challenge) qs.set("challenge", challenge);
  const q = qs.toString();
  return `${base}/api/ws/mp/${game}${q ? "?" + q : ""}`;
};

// Phase 14 — jokes
export const jokeCategories = () => api.get("/jokes/categories").then((r) => r.data);
export const jokeGenerate = (payload) => api.post("/jokes/generate", payload).then((r) => r.data);
export const jokeDaily = () => api.get("/jokes/daily").then((r) => r.data);
export const jokeTrending = (limit = 12) => api.get("/jokes/trending", { params: { limit } }).then((r) => r.data);
export const jokeHistory = () => api.get("/jokes/history").then((r) => r.data);
export const jokeFavorites = () => api.get("/jokes/favorites").then((r) => r.data);
export const jokeFavorite = (joke_id) => api.post("/jokes/favorite", { joke_id }).then((r) => r.data);
export const jokeReact = (joke_id, reaction) => api.post("/jokes/react", { joke_id, reaction }).then((r) => r.data);
export const jokeShare = (joke_id) => api.post("/jokes/share", { joke_id }).then((r) => r.data);
export const jokeSearch = (q, category) => api.get("/jokes/search", { params: { q, category } }).then((r) => r.data);

// Phase 14 — one-click app update
export const devOneClickUpdate = (slug) => api.post(`/dev/apps/${slug}/update`).then((r) => r.data);

// Phase 14 — roadmap
export const roadmapList = (sort = "top", status = "", category = "") =>
  api.get("/roadmap/requests", { params: { sort, status, category } }).then((r) => r.data);
export const roadmapCreate = (payload) => api.post("/roadmap/requests", payload).then((r) => r.data);
export const roadmapUpvote = (id) => api.post(`/roadmap/requests/${id}/upvote`).then((r) => r.data);
export const roadmapMyUpvotes = () => api.get("/roadmap/my-upvotes").then((r) => r.data);
export const roadmapAdminUpdate = (id, payload) => api.patch(`/roadmap/requests/${id}`, payload).then((r) => r.data);
export const roadmapAdminDelete = (id) => api.delete(`/roadmap/requests/${id}`).then((r) => r.data);

// Phase 15 — JixxeoTV
export const tvList = (params = {}) => api.get("/tv/channels", { params }).then((r) => r.data);
export const tvGet = (cid) => api.get(`/tv/channels/${cid}`).then((r) => r.data);
export const tvCreate = (payload) => api.post("/tv/channels", payload).then((r) => r.data);
export const tvUpdate = (cid, payload) => api.patch(`/tv/channels/${cid}`, payload).then((r) => r.data);
export const tvDelete = (cid) => api.delete(`/tv/channels/${cid}`).then((r) => r.data);
export const tvFavorites = () => api.get("/tv/favorites").then((r) => r.data);
export const tvToggleFavorite = (channel_id) => api.post("/tv/favorites/toggle", { channel_id }).then((r) => r.data);
export const tvHistory = () => api.get("/tv/history").then((r) => r.data);
export const tvRecordHistory = (channel_id, position_sec = 0) =>
  api.post("/tv/history", { channel_id, position_sec }).then((r) => r.data);
export const tvContinueWatching = () => api.get("/tv/continue-watching").then((r) => r.data);
export const tvSettings = () => api.get("/tv/settings").then((r) => r.data);
export const tvSetWatermark = (payload) => api.patch("/tv/settings/watermark", payload).then((r) => r.data);
export const tvBulkImport = (channels) => api.post("/tv/channels/bulk-import", { channels }).then((r) => r.data);
export const tvPreflight = (url) => api.post("/tv/preflight", { url }).then((r) => r.data);
export const adminEditApp = (slug, updates) => api.patch(`/admin/apps/${slug}`, { updates }).then((r) => r.data);
// Phase 18 — user manager & M3U import
export const adminUpdateUser = (user_id, updates) => api.patch(`/admin/users/${user_id}`, updates).then((r) => r.data);
export const adminDeleteUser = (user_id) => api.delete(`/admin/users/${user_id}`).then((r) => r.data);
export const tvM3UImport = (payload) => api.post("/tv/channels/m3u-import", payload).then((r) => r.data);

// Phase 16 — Watch Party
export const tvPartyCreate = (channel_id) => api.post("/tv/party", { channel_id }).then((r) => r.data);
export const tvPartyGet = (pid) => api.get(`/tv/party/${pid}`).then((r) => r.data);
export const tvPartyWsUrl = (pid) => {
  const base = BACKEND_URL.replace(/^http/, "ws");
  return `${base}/api/ws/tv/party/${pid}`;
};

// cashout
export const cashoutInfo = () => api.get("/cashout/info").then((r) => r.data);
export const cashoutRequest = (coins, method, handle) => api.post("/cashout", { coins, method, handle }).then((r) => r.data);

// comment reporting
export const reportComment = (id, reason) => api.post(`/comments/${id}/report`, { reason }).then((r) => r.data);

// admin
export const adminOverview = () => api.get("/admin/overview").then((r) => r.data);
export const adminAnalytics = (days = 30) => api.get("/admin/analytics/timeseries", { params: { days } }).then((r) => r.data);
export const adminApps = (params) => api.get("/admin/apps", { params }).then((r) => r.data);
export const adminApprove = (slug) => api.post(`/admin/apps/${slug}/approve`).then((r) => r.data);
export const adminSuspend = (slug, reason) => api.post(`/admin/apps/${slug}/suspend`, { reason }).then((r) => r.data);
export const adminFeature = (slug) => api.post(`/admin/apps/${slug}/feature`).then((r) => r.data);
export const adminUnfeature = (slug) => api.post(`/admin/apps/${slug}/unfeature`).then((r) => r.data);
export const adminEditorsChoice = (slug) => api.post(`/admin/apps/${slug}/editors-choice`).then((r) => r.data);
export const adminSoftDelete = (slug, reason) => api.post(`/admin/apps/${slug}/delete`, { reason }).then((r) => r.data);
export const adminRestore = (slug) => api.post(`/admin/apps/${slug}/restore`).then((r) => r.data);
export const adminPurge = (slug) => api.delete(`/admin/apps/${slug}`, { params: { confirm: slug } }).then((r) => r.data);
export const adminReportedComments = () => api.get("/admin/comments", { params: { reported_only: true } }).then((r) => r.data);
export const adminDeleteComment = (id) => api.delete(`/admin/comments/${id}`).then((r) => r.data);
export const adminClearReports = (id) => api.post(`/admin/comments/${id}/clear-reports`).then((r) => r.data);
export const adminUsers = (params) => api.get("/admin/users", { params }).then((r) => r.data);
export const adminCashouts = (status) => api.get("/admin/cashouts", { params: status ? { status } : {} }).then((r) => r.data);
export const adminCashoutAction = (id, action, note) => api.post(`/admin/cashouts/${id}`, { action, note }).then((r) => r.data);

// play time rewards + loops
export const ptrClaim = (slug, seconds) => api.post("/ptr/claim", { slug, seconds }).then((r) => r.data);
export const saveLoop = (name, bpm, notes) => api.post("/vibes/loops", { name, bpm, notes }).then((r) => r.data);
export const myLoops = () => api.get("/me/loops").then((r) => r.data);
export const publicLoops = () => api.get("/vibes/loops").then((r) => r.data);
export const getLoop = (id) => api.get(`/vibes/loops/${id}`).then((r) => r.data);
export const playedLoop = (id) => api.post(`/vibes/loops/${id}/play`).then((r) => r.data);
export const deleteLoop = (id) => api.delete(`/vibes/loops/${id}`).then((r) => r.data);
export const tipLoop = (id, coins, message) => api.post(`/vibes/loops/${id}/tip`, { coins, message }).then((r) => r.data);

// streaks
export const streakCheckin = () => api.post("/streak/checkin").then((r) => r.data);
export const streakStatus = () => api.get("/streak").then((r) => r.data);

// draw gallery
export const saveArtwork = (title, image_data_url) => api.post("/draw/artworks", { title, image_data_url }).then((r) => r.data);
export const listArtworks = (params) => api.get("/draw/artworks", { params }).then((r) => r.data);
export const myArtworks = () => api.get("/me/artworks").then((r) => r.data);
export const deleteArtwork = (id) => api.delete(`/draw/artworks/${id}`).then((r) => r.data);
export const likeArtwork = (id) => api.post(`/draw/artworks/${id}/like`).then((r) => r.data);

// push
export const pushVapidKey = () => api.get("/push/vapid-key").then((r) => r.data);
export const pushSubscribe = (subscription, user_agent) => api.post("/push/subscribe", { subscription, user_agent }).then((r) => r.data);
export const pushUnsubscribe = (endpoint) => api.post("/push/unsubscribe", { endpoint }).then((r) => r.data);
export const pushGetPrefs = () => api.get("/push/preferences").then((r) => r.data);
export const pushSetPrefs = (preferences) => api.put("/push/preferences", { preferences }).then((r) => r.data);
export const pushTest = () => api.post("/push/test").then((r) => r.data);

// racer
export const racerSubmit = (lap_time_ms, total_time_ms, laps) => api.post("/racer/score", { lap_time_ms, total_time_ms, laps }).then((r) => r.data);
export const racerLeaderboard = (limit = 20) => api.get("/racer/leaderboard", { params: { limit } }).then((r) => r.data);
export const racerMe = () => api.get("/racer/me").then((r) => r.data);

// realtime notifications
export const wsUrl = () => {
  const base = BACKEND_URL.replace(/^http/, "ws");
  return `${base}/api/ws/notifications`;
};

export const formatDownloads = (n) => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return `${n}`;
};

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// launches + reviews
const _clientId = () => {
  try {
    let id = localStorage.getItem("jixxeo_cid");
    if (!id) { id = Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("jixxeo_cid", id); }
    return id;
  } catch { return ""; }
};
export const trackLaunch = (slug) => {
  if (!slug) return;
  try {
    const k = `jixxeo_launch_${slug}`;
    const last = Number(sessionStorage.getItem(k) || 0);
    if (Date.now() - last < 10 * 60 * 1000) return;
    sessionStorage.setItem(k, String(Date.now()));
  } catch {}
  if (!navigator.onLine) return queueLaunch(slug);
  api.post(`/apps/${slug}/launch`, { client_id: _clientId() }).catch((e) => { if (!e.response) queueLaunch(slug); });
};
export const fetchReviews = (slug, sort) => api.get(`/apps/${slug}/reviews`, { params: { sort } }).then((r) => r.data);
export const saveReview = (slug, data) => api.post(`/apps/${slug}/reviews`, data).then((r) => r.data);
export const deleteMyReview = (slug) => api.delete(`/apps/${slug}/reviews/me`).then((r) => r.data);
export const reportReview = (id, reason) => api.post(`/reviews/${id}/report`, { reason }).then((r) => r.data);
